// src/services/chatBotService.ts
import axios from 'axios';
import { RAG_ASK, RAG_HISTORY } from '../constants/routes';
import localStorageService from './localStorageService';

/**
 * REQUEST PAYLOAD - Backend now manages session state!
 * No need to send session_id - backend handles it automatically
 * Just send: question, sport_code, entity, username
 */
interface AskQuestionPayload {
  question: string;
  top_k?: number;
  sport_code?: string;
  entity?: string;
  username?: string;
  // ✅ NO session_id here anymore!
}

/**
 * RESPONSE - Backend returns session_id for tracking/analytics
 * Optional: Store it in localStorage for debugging/analytics only
 * Don't use it for request payloads - backend handles session state!
 */
interface AskQuestionResponse {
  answer: string;
  sources: string[];
  session_id: string; // ✅ Store this if needed: localStorage.setItem(`${sportCode}_last_session`, session_id)
  message_id: number;
  facts_used?: number;
  tokens_used?: number;
}

export interface HistoryMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  sources?: string[];
}

export interface GetHistoryResponse {
  messages: HistoryMessage[];
  session_id: string;
}

class ChatBotService {
  /**
   * Send a query to the RAG backend
   * 
   * ✅ SIMPLIFIED: No session management needed!
   * Backend automatically:
   * - Manages session state
   * - Tracks context across messages
   * - Handles sport switching (resets context if sport_code changes)
   * 
   * Frontend just needs to pass: question, sport_code, entity, username
   * That's it!
   */
  public askQuestion = async (
    payload: AskQuestionPayload
  ): Promise<AskQuestionResponse> => {
    try {
      const userInfo = localStorageService.getUserInfo();
      const token = userInfo.access_token || '';

      const response = await axios.post(RAG_ASK, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
      });

      return response.data;
    } catch (error: any) {
      throw error.response || error;
    }
  };

  public getHistory = async (
    sessionId: string,
    entity?: string,
    sportCode?: string
  ): Promise<GetHistoryResponse> => {
    try {
      const userInfo = localStorageService.getUserInfo();
      const token = userInfo.access_token || '';

      const params: Record<string, string> = { session_id: sessionId };
      if (entity) params.entity = entity;
      if (sportCode) params.sport_code = sportCode;

      const response = await axios.get(RAG_HISTORY, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        params,
      });

      return response.data;
    } catch (error: any) {
      throw error.response || error;
    }
  };
}

export default ChatBotService;